import React, { useState, useEffect } from 'react';
import {
  Layout, Menu, theme, Avatar, Dropdown, Badge, Button, Tag,
  Drawer, ConfigProvider, Switch, Tooltip, Space,
} from 'antd';
import type { ThemeConfig } from 'antd';
import {
  DashboardOutlined, CreditCardOutlined, ApiOutlined, TeamOutlined,
  EnvironmentOutlined, AlertOutlined, MessageOutlined,
  FileTextOutlined, BarChartOutlined, SettingOutlined,
  BellOutlined, UserOutlined, LogoutOutlined, GiftOutlined,
  WifiOutlined, ThunderboltOutlined, SafetyOutlined,
  MenuOutlined, BulbOutlined, MoonFilled, SunFilled,
} from '@ant-design/icons';
import { useAuthStore } from './store/authStore';
import { getRoleLabel, getRoleColor } from './api/authApi';
import { ROLE_MENUS } from './types';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import CardManage from './pages/CardManage';
import ESim from './pages/ESim';
import PackagePool from './pages/PackagePool';
import UserManage from './pages/UserManage';
import Commission from './pages/Commission';
import Location from './pages/Location';
import AlertManage from './pages/AlertManage';
import SmsManage from './pages/SmsManage';
import WorkOrder from './pages/WorkOrder';
import Report from './pages/Report';
import ApiManage from './pages/ApiManage';
import './App.css';

const { Header, Sider, Content } = Layout;

const ALL_MENUS = [
  { key: 'dashboard',  icon: <DashboardOutlined />,    label: '首页总览' },
  { key: 'card',       icon: <CreditCardOutlined />,   label: '卡片管理' },
  { key: 'esim',       icon: <WifiOutlined />,         label: 'eSIM管理' },
  { key: 'package',    icon: <ThunderboltOutlined />,  label: '套餐与流量池' },
  { key: 'user',       icon: <TeamOutlined />,         label: '用户/渠道管理' },
  { key: 'commission', icon: <GiftOutlined />,         label: '分润管理' },
  { key: 'location',   icon: <EnvironmentOutlined />,  label: '定位管理' },
  { key: 'alert',      icon: <AlertOutlined />,        label: '预警与风控' },
  { key: 'sms',        icon: <MessageOutlined />,      label: '短信管理' },
  { key: 'workorder',  icon: <FileTextOutlined />,     label: '工单管理' },
  { key: 'report',     icon: <BarChartOutlined />,     label: '报表管理' },
  { key: 'api',        icon: <ApiOutlined />,          label: 'API管理' },
  { key: 'settings',   icon: <SettingOutlined />,      label: '系统设置' },
];

const pageMap: Record<string, React.ReactNode> = {
  dashboard: <Dashboard />,
  card:      <CardManage />,
  esim:      <ESim />,
  package:   <PackagePool />,
  user:      <UserManage />,
  commission:<Commission />,
  location:  <Location />,
  alert:     <AlertManage />,
  sms:       <SmsManage />,
  workorder: <WorkOrder />,
  report:    <Report />,
  api:       <ApiManage />,
};

// 暗黑主题 token
const darkToken: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorPrimary: '#1677ff',
    borderRadius: 8,
  },
};
const lightToken: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: '#1677ff',
    borderRadius: 8,
  },
};

// 检测移动端
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const AppInner: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKey, setSelectedKey] = useState('dashboard');
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('cmp-dark') === '1';
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { token } = theme.useToken();
  const { user, isLoggedIn, logout } = useAuthStore();
  const isMobile = useIsMobile();

  // 暗黑模式持久化
  useEffect(() => {
    localStorage.setItem('cmp-dark', darkMode ? '1' : '0');
  }, [darkMode]);

  // 访问 /?logout=1 时自动清除登录状态
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('logout') === '1') {
      logout();
      window.history.replaceState({}, '', '/');
    }
  }, [logout]);

  // 未登录 → 显示登录页
  if (!isLoggedIn || !user) {
    return <LoginPage onLogin={() => {}} />;
  }

  const allowedMenuKeys = ROLE_MENUS[user.role] || [];
  const menuItems = ALL_MENUS.filter(m => allowedMenuKeys.includes(m.key));

  const userMenuItems = {
    items: [
      {
        key: 'info', disabled: true, label: (
          <div style={{ padding: '4px 0' }}>
            <div style={{ fontWeight: 600 }}>{user.name}</div>
            <Tag color={getRoleColor(user.role)} style={{ marginTop: 4 }}>{getRoleLabel(user.role)}</Tag>
            <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>租户：{user.tenantName}</div>
          </div>
        ),
      },
      { type: 'divider' as const },
      { key: 'profile', icon: <UserOutlined />, label: '个人信息' },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
    ],
    onClick: ({ key }: { key: string }) => { if (key === 'logout') logout(); },
  };

  const menuComp = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[selectedKey]}
      items={menuItems}
      onClick={({ key }) => { setSelectedKey(key); setDrawerOpen(false); }}
      style={{ borderRight: 'none' }}
    />
  );

  const siderHeader = (
    <>
      <div className="logo">
        <SafetyOutlined style={{ fontSize: 22, color: '#1677ff' }} />
        {(!collapsed || isMobile) && (
          <span style={{ color: '#fff', marginLeft: 10, fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>
            CMP平台
          </span>
        )}
      </div>
      {(!collapsed || isMobile) && (
        <div style={{ padding: '6px 16px 8px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Tag color={getRoleColor(user.role)} style={{ fontSize: 11 }}>{getRoleLabel(user.role)}</Tag>
          <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, marginLeft: 4 }}>{user.tenantName}</span>
        </div>
      )}
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 桌面端侧边栏 */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          style={{ background: '#001529', position: 'sticky', top: 0, height: '100vh', overflow: 'auto' }}
          width={220}
        >
          {siderHeader}
          {menuComp}
        </Sider>
      )}

      {/* 移动端抽屉菜单 */}
      {isMobile && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          width={220}
          styles={{ body: { padding: 0, background: '#001529' }, header: { display: 'none' } }}
        >
          {siderHeader}
          {menuComp}
        </Drawer>
      )}

      <Layout>
        <Header style={{
          padding: '0 16px',
          background: token.colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          position: 'sticky', top: 0, zIndex: 100,
          height: 56,
        }}>
          <Space size={8}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerOpen(true)}
              />
            )}
            <span style={{ fontSize: 15, fontWeight: 700, color: token.colorTextHeading }}>
              {ALL_MENUS.find(m => m.key === selectedKey)?.label || '物联网卡管理平台'}
            </span>
          </Space>

          <Space size={4}>
            {/* 暗黑模式切换 */}
            <Tooltip title={darkMode ? '切换浅色' : '切换暗黑'}>
              <Button
                type="text"
                icon={darkMode ? <SunFilled style={{ color: '#fadb14', fontSize: 18 }} /> : <MoonFilled style={{ fontSize: 18 }} />}
                onClick={() => setDarkMode(d => !d)}
              />
            </Tooltip>
            <Badge count={5} size="small">
              <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} />
            </Badge>
            <Dropdown menu={userMenuItems} placement="bottomRight" trigger={['click']}>
              <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '0 4px' }}>
                <Avatar style={{ backgroundColor: '#1677ff', fontSize: 13 }}>
                  {user.name[0]}
                </Avatar>
                {!isMobile && (
                  <div style={{ lineHeight: 1.3 }}>
                    <div style={{ fontSize: 13, color: token.colorText, fontWeight: 500 }}>{user.name}</div>
                    <div style={{ fontSize: 11, color: token.colorTextSecondary }}>{getRoleLabel(user.role)}</div>
                  </div>
                )}
              </div>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ margin: isMobile ? '8px' : '16px', minHeight: 'calc(100vh - 56px)' }}>
          {pageMap[selectedKey] || (
            <div style={{ padding: 48, textAlign: 'center', color: '#999' }}>
              <SettingOutlined style={{ fontSize: 48 }} />
              <p style={{ marginTop: 16 }}>该功能模块正在建设中...</p>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

// 外层包一层 ConfigProvider 控制主题
const App: React.FC = () => {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('cmp-dark') === '1');

  // 监听 localStorage 变化（AppInner 修改时同步）
  useEffect(() => {
    const handler = () => setDarkMode(localStorage.getItem('cmp-dark') === '1');
    window.addEventListener('storage', handler);
    // 轮询同步（同页面内 localStorage 不触发 storage 事件）
    const timer = setInterval(() => {
      setDarkMode(localStorage.getItem('cmp-dark') === '1');
    }, 300);
    return () => { window.removeEventListener('storage', handler); clearInterval(timer); };
  }, []);

  return (
    <ConfigProvider theme={darkMode ? darkToken : lightToken} locale={{ locale: 'zh-cn' } as any}>
      <AppInner />
    </ConfigProvider>
  );
};

export default App;
